import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  FileText,
  Filter,
  User,
  Calendar,
  Activity
} from 'lucide-react';

const actionLabels = {
  user_created: 'Utilisateur créé',
  user_login: 'Connexion',
  beds24_connected: 'Beds24 connecté',
  beds24_sync_completed: 'Sync terminée',
  beds24_sync_error: 'Erreur sync',
  review_job_created: 'Job créé',
  review_job_updated: 'Job mis à jour',
  review_job_processed: 'Job traité',
  review_job_approved: 'Job approuvé',
  review_job_rejected: 'Job rejeté',
  review_job_published: 'Job publié',
  review_job_error: 'Erreur job',
  draft_generated: 'Brouillon généré',
  draft_edited: 'Brouillon édité',
  settings_updated: 'Paramètres modifiés'
};

export const LogsPage = () => {
  const api = useApi();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [entityFilter, setEntityFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    try {
      const params = entityFilter !== 'all' ? `?entity_type=${entityFilter}` : '';
      const response = await api.get(`/audit-logs${params}`);
      setLogs(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, entityFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchLogs();
  };

  return (
    <MainLayout>
      <Header
        title="Logs & Audit"
        subtitle="Historique des actions et événements"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="logs-page">
        {/* Filter */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter size={16} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-500">Filtrer par type</span>
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-[180px]" data-testid="filter-entity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="user">Utilisateurs</SelectItem>
                  <SelectItem value="integration">Intégrations</SelectItem>
                  <SelectItem value="review_job">Jobs</SelectItem>
                  <SelectItem value="review_draft">Brouillons</SelectItem>
                  <SelectItem value="settings">Paramètres</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1" />
              <span className="text-sm text-slate-500">{logs.length} entrées</span>
            </div>
          </CardContent>
        </Card>

        {/* Logs list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : logs.length === 0 ? (
          <Card className="bg-white border-slate-100">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun log à afficher</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {logs.map((log) => (
                  <div key={log.id} className="p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        log.entity_type === 'user' ? 'bg-blue-100' :
                        log.entity_type === 'integration' ? 'bg-purple-100' :
                        log.entity_type === 'review_job' ? 'bg-emerald-100' :
                        log.entity_type === 'review_draft' ? 'bg-amber-100' :
                        'bg-slate-100'
                      }`}>
                        {log.entity_type === 'user' ? (
                          <User size={14} className="text-blue-600" />
                        ) : log.entity_type === 'integration' ? (
                          <Activity size={14} className="text-purple-600" />
                        ) : (
                          <FileText size={14} className="text-slate-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900 text-sm">
                            {actionLabels[log.action] || log.action}
                          </span>
                          <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">
                            {log.entity_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(log.created_at).toLocaleString('fr-FR')}
                          </span>
                          {log.entity_id && (
                            <span className="font-mono truncate max-w-[200px]">
                              ID: {log.entity_id.substring(0, 8)}...
                            </span>
                          )}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="mt-2 p-2 bg-slate-50 rounded text-xs font-mono text-slate-600 overflow-x-auto">
                            {JSON.stringify(log.details, null, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default LogsPage;
